<template>
  <div v-intersect class="filter-tile" @intersect:enter="onShow">
    <div class="filter-tile__search-wrapper">
      <!-- SEARCH -->
      <transition name="filter-input">
        <BaseInput
          v-if="showMore"
          v-model.trim="searchValue"
          class="filter-tile__search"
          :placeholder="$t('catalog.search.placeholder')"
        />
      </transition>
    </div>
    <!-- TILE -->
    <div class="tiles">
      <!--      <transition-group name="filter-tile" tag="div">-->
      <template v-if="isFilterWithLink">
        <a
          v-for="item in items"
          :key="item.value"
          :href="getLinkFilter(item.value)"
          rel="nofollow"
          class="tile-link"
          @click.prevent="onChange(item)"
          @touchdown.prevent="onChange(item)"
        >
          <span
            class="tile-item"
            :class="{
              'tile-item--selected': hasChecked(item),
              'tile-item--disabled': !item.count
            }"
          >{{ item.label }}</span>
        </a>
      </template>

      <template v-else>
        <span
          v-for="item in items"
          :key="item.value"
          class="tile-link tile-item"
          :class="{
            'tile-item--selected': hasChecked(item),
            'tile-item--disabled': !item.count
          }"
          @click.prevent="onChange(item)"
          @touchdown.prevent="onChange(item)"
        >{{ item.label }}</span>
      </template>
      <!--      </transition-group>-->

      <div v-if="showMore && countOfSorted === 0" class="tile-no-results">
        {{ $t('catalog.filters.no_results') }}
      </div>
    </div>

    <!-- SHOW MORE -->
    <a
      v-if="exceedsLimit"
      href="#"
      rel="nofollow"
      class="filter-tile__more link"
      @click.prevent="toggleShowMore"
    >{{
      showMore
        ? $t('catalog.filters.hide_more_filters')
        : $t('catalog.filters.show_more_filters', { count: countOfSorted })
    }}</a>
  </div>
</template>

<script setup lang="ts">
import { BRAND_GROUP_FILTER_CODE, CONDITION_FILTER_CODE } from '@/constants/common'
import type { CatalogFilter, CatalogFilterItem, FilterItemChangePayload } from '~/types/catalog'

type Props = {
  filter: CatalogFilter
  selected: string
  limit?: number
  showMore?: boolean
}

type Emits = {
  (e: 'show-more', value: boolean): void
  (e: 'on-change' | 'on-show', value: FilterItemChangePayload): void
}

const props = withDefaults(defineProps<Props>(), {
  limit: 20
})
const emit = defineEmits<Emits>()
const localePath = useLocalePath()
const route = useRoute()

const filterWithLinks = inject<boolean>('filterWithLinks', false)

const searchValue = shallowRef('')

const searchIsUsed = computed(() => {
  return searchValue.value.length > 0
})

const selectedKeys = computed(() => new Set(props.selected.split(',')))

const filterItems = computed<CatalogFilterItem[]>(() => {
  const items: CatalogFilterItem[] = props.filter.items.filter((item: CatalogFilterItem) => item.slug !== 'drugie')

  if (!searchIsUsed.value) {
    return items
  }
  const regSearch: RegExp = new RegExp(searchValue.value, 'i')

  return items.filter((item: CatalogFilterItem) => regSearch.test(item.label))
})

// Відсортованні фільтри по кількості
const sortedItems = computed<CatalogFilterItem[]>(() => {
  return [...filterItems.value].sort((a, b) => Number(b.count > 0) - Number(a.count > 0))
})
// кількість відсортованих фільтрів
const countOfSorted = computed(() => sortedItems.value.length)
// кількість відсортованих фільтрів перевищує ліміт показу
const exceedsLimit = computed(() => countOfSorted.value > props.limit)

const items = computed<CatalogFilterItem[]>(() => {
  return exceedsLimit.value && !props.showMore
    ? sortedItems.value.slice(0, props.limit)
    : sortedItems.value
})

const isFilterWithLink = computed(() => {
  if (!filterWithLinks) {
    return filterWithLinks
  }
  const filterCode = props.filter.code

  return (
    new RegExp(CONDITION_FILTER_CODE, 'i').test(filterCode) ||
    new RegExp(BRAND_GROUP_FILTER_CODE, 'i').test(filterCode)
  )
})

const getLinkFilter = (value: string) => {
  const values = props.selected ? `${props.selected},${value}` : value
  const categoryFilters: string = `${props.filter.code}=${values}`
  const { path, params } = unref(route)

  if (params.categoryFilters) {
    return localePath({
      params: {
        ...params,
        categoryFilters
      }
    })
  }
  return localePath(`${path}/${categoryFilters}`)
}

const hasChecked = ({ value }: CatalogFilterItem) => {
  return selectedKeys.value.has(value)
}

const toggleShowMore = () => {
  emit('show-more', !props.showMore)
}

const onChange = ({ value, label }: CatalogFilterItem) => {
  emit('on-change', {
    filter_code: props.filter.code,
    filter_name: props.filter.name,
    value,
    label
  })
}

const onShow = () => {
  emit('on-show', {
    filter_code: props.filter.code,
    filter_name: props.filter.name,
  })
}

</script>

<style lang="scss" scoped>
.filter-tile {
  &__search-wrapper {
    position: relative;
  }
  &__search {
    margin-bottom: 1.6rem;
  }

  &__more {
    font-size: 1.2rem;
  }
}

.tiles {
  overflow: hidden;
  position: relative;
  margin-right: -1rem;
}

.tile-link {
  display: inline-block;
  text-transform: none;
  margin: 0 1rem 1rem 0;
}

.tile-item {
  border: 1px solid #e7e7e7;
  color: $font-color;
  padding: 0.3rem 0.9rem;
  border-radius: 4px;
  font-size: 1.2rem;
  display: inline-block;
  cursor: pointer;
  min-width: 2.8rem;
  box-sizing: border-box;

  &--selected {
    border: 2px solid $primary-color;
    font-weight: 500;
  }

  &--disabled {
    opacity: 0.4;
  }
}

/*animations*/
.filter-input {
  &-enter-active,
  &-leave-active {
    transition: $transition-time;
  }

  &-enter {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
    overflow: hidden;
  }

  &-enter-to {
    opacity: 1;
    max-height: 4.2rem;
    margin-bottom: 1.6rem;
  }

  &-leave {
    opacity: 1;
    max-height: 4.2rem;
  }

  &-leave-to {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
  }
}

.filter-tile {
  &-enter-active,
  &-move {
    transition: $transition-time cubic-bezier(0.59, 0.12, 0.34, 0.95);
    transition-property: opacity, transform;
  }

  &-leave-active {
    transition: 70ms cubic-bezier(0.59, 0.12, 0.34, 0.95);
    transition-property: opacity, transform;
  }

  &-enter {
    opacity: 0;
    transform: translateY(-100%);
  }

  &-leave-to {
    opacity: 0;
    transform: translateY(-100%);
    transform-origin: center top;
  }
}
/*  */
</style>
